import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getMaterials } from "../../services/apiService";
import MaterialForm from "./MaterialForm";
import WorkbenchShell from "../workbench/WorkbenchShell";
import "./adminShell.css";

/**
 * Dedicated page wrapper for MaterialForm.
 * Handles both create (/admin/materials/new) and edit (/admin/materials/:id/edit) routes.
 */
function MaterialFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [material, setMaterial] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const isEditMode = !!id;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (isEditMode) {
          // Fetch all materials and find the one to edit
          const materialsData = await getMaterials();
          const materialToEdit = materialsData.find(
            (m) => m.id === parseInt(id),
          );
          if (materialToEdit) {
            setMaterial(materialToEdit);
          } else {
            setError(`Material with ID ${id} not found.`);
          }
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError(err.message || "Failed to load data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, isEditMode]);

  const handleMaterialAdded = () => {
    navigate("/admin/materials");
  };

  const handleCancel = () => {
    navigate("/admin/materials");
  };

  const title = isEditMode
    ? `Edit material: ${material?.name || ""}`.trim()
    : "Add new material";

  // Centre-only: one form, nothing to list beside it and nothing to trace.
  const backAction = (
    <button
      type="button"
      className="wb-tbtn"
      onClick={() => navigate("/admin/materials")}
    >
      ← Back to materials
    </button>
  );

  if (isLoading) {
    return (
      <WorkbenchShell
        variant="admin"
        title={title}
        actions={backAction}
        center={<p className="wb-empty">Loading…</p>}
      />
    );
  }

  if (error) {
    return (
      <WorkbenchShell
        variant="admin"
        title={title}
        actions={backAction}
        center={
          <p className="wb-empty wb-empty--error" role="alert">
            {error}
          </p>
        }
      />
    );
  }

  return (
    <WorkbenchShell
      variant="admin"
      title={title}
      subtitle={isEditMode ? "Materials" : "Materials"}
      actions={backAction}
      rail={null}
      inspector={null}
      centerHeader={{ label: title }}
      center={
        <div className="material-form-page">
          <MaterialForm
            key={isEditMode ? material?.id : "new"}
            materialToEdit={isEditMode ? material : null}
            onMaterialAdded={handleMaterialAdded}
            onCancel={handleCancel}
          />
        </div>
      }
    />
  );
}

export default MaterialFormPage;
